import React, { useMemo } from 'react';

import { StyledReserveWarningAlert, StyledAlertTitle } from '@this/components/shared/reserve_ui_components';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Link } from '@this/components/shared/ui/navigations/link';
import type Hotel from '@this/src/domain/hotel/hotel2';
import { AvailableRoomsText } from '@this/components/shared/hotel/available_rooms_text';
import { digits } from '@this/src/util';
import { formatDateRangeWithWeekday } from '@this/src/util/date';
import Text from '../../../shared/text/text';
import StayPlanWarning from '../../select/shared/stay_plan_warning';

type Props = {
  hotel: Hotel | null | undefined;
  isPackage: boolean;
  hotelPrice: number;
  itemTestIdPrefix: string;
  peoplenum: number;
  roomnum: number | undefined;
};

const ReserveConfirmHotelTr: React.FC<Props> = ({
  hotel,
  isPackage,
  hotelPrice,
  itemTestIdPrefix,
  peoplenum,
  roomnum
}) => {
  const styles = useMemo(() => getStyles(), []);

  try {
    if (!hotel) return <span>-</span>;

    const refundText = hotel.refundable === null ? 'お問合せください' : hotel.refundable ? '可' : '不可';
    const isRefundNg = hotel.refundable === false;
    const displayCancelPolicy = hotel.displayCancelPolicy;

    return (
      <>
        <div data-testid={`${itemTestIdPrefix}-name`}>
          <Text text={hotel.name} />
        </div>
        <div style={styles.textDetail}>
          <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-date-range-value`}>
            {formatDateRangeWithWeekday(
              hotel.checkinDate?.format('YYYY-MM-DD'),
              hotel.checkoutDate?.format('YYYY-MM-DD'),
              'YYYY-MM-DD',
              ' 〜 '
            )}
          </span>
          <br />
          {isPackage ? (
            <span data-testid={`${itemTestIdPrefix}-occupancy-value`}>{`${peoplenum}名/${roomnum}室`}</span>
          ) : (
            <div className="flex">
              <span data-wovn-ignore>{digits(hotelPrice)}</span>円({peoplenum}名/{roomnum}室)
            </div>
          )}
          {hotel.mandatoryTax && hotel.mandatoryTax > 0 ? (
            <p data-wovn-ignore>{digits(hotel.mandatoryTax)}円(現地徴収料)</p>
          ) : null}
          <p data-testid={`${itemTestIdPrefix}-stay-row`}>
            {hotel.stayDays}泊{hotel.days}日
          </p>
          <p data-testid={`${itemTestIdPrefix}-checkin-row`}>
            <span data-testid={`${itemTestIdPrefix}-checkin-label`}>チェックイン　：</span>
            <span data-testid={`${itemTestIdPrefix}-checkin-value`}>{hotel.checkinText}</span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-checkout-row`}>
            <span data-testid={`${itemTestIdPrefix}-checkout-label`}>チェックアウト：</span>
            <span data-testid={`${itemTestIdPrefix}-checkout-value`}>{hotel.checkoutText}</span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-breakfast-row`}>
            <span data-testid={`${itemTestIdPrefix}-breakfast-label`}>朝食：</span>
            <span data-testid={`${itemTestIdPrefix}-breakfast-value`}>
              {hotel.breakfast == null ? 'お問合せください' : hotel.breakfast ? '有り' : '無し'}
            </span>
          </p>
          <p data-testid={`${itemTestIdPrefix}-smoke-row`}>
            <span data-testid={`${itemTestIdPrefix}-smoke-label`}>タバコ：</span>
            <span
              data-testid={`${itemTestIdPrefix}-smoke-value`}
              style={hotel.smoke === true ? styles.smokeAlert : undefined}
            >
              {hotel.smoke == null ? 'お問合せください' : hotel.smoke ? '喫煙' : '禁煙'}
            </span>
          </p>
          {hotel.availableRooms != null && (
            <p data-testid={`${itemTestIdPrefix}-available-rooms-row`}>
              <span data-testid={`${itemTestIdPrefix}-available-rooms-label`}>空室数：</span>
              <span data-testid={`${itemTestIdPrefix}-available-rooms-value`}>
                <AvailableRoomsText availableRooms={hotel.availableRooms} />
              </span>
              （検索時点の情報です）
            </p>
          )}
          <p data-testid={`${itemTestIdPrefix}-plan-name-row`}>
            <span data-testid={`${itemTestIdPrefix}-plan-name-label`}>宿泊プラン：</span>
            <span data-testid={`${itemTestIdPrefix}-plan-name-value`}>{hotel.planName || '-'}</span>
          </p>
          {hotel.selectedPlanRestricted && (
            <div style={styles.stayPlanWarningWrapper}>
              <StayPlanWarning variant="text" restricted={hotel.selectedPlanRestricted} />
            </div>
          )}
          {hotel.detailPath && (
            <p data-testid={`${itemTestIdPrefix}-detail-link-row`}>
              <span data-testid={`${itemTestIdPrefix}-detail-link-label`}>宿泊詳細情報：</span>
              <Link
                href={hotel.detailPath}
                isExternal
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`${itemTestIdPrefix}-detail-link`}
              >
                詳細はコチラ
              </Link>
            </p>
          )}
          {hotel.roomType && (
            <p data-testid={`${itemTestIdPrefix}-room-type-row`}>
              <span data-testid={`${itemTestIdPrefix}-room-type-label`}>部屋タイプ：</span>
              <span data-testid={`${itemTestIdPrefix}-room-type-value`}>{hotel.roomType}</span>
            </p>
          )}
          <p data-testid={`${itemTestIdPrefix}-bed-type-row`}>
            <span data-testid={`${itemTestIdPrefix}-bed-type-label`}>ベッドタイプ：</span>
            <span data-testid={`${itemTestIdPrefix}-bed-type-value`}>{hotel.displayBedType}</span>
          </p>
          {hotel.hasMultipleBedTypes && (
            <div style={{ margin: '8px 0' }}>
              <StyledReserveWarningAlert icon={<ErrorOutlineIcon />} variant="filled" severity="error">
                <StyledAlertTitle>ベッドタイプについて</StyledAlertTitle>
                <div>
                  <p style={{ margin: '4px 0' }}>{hotel.bedTypeNoticeMessage}</p>
                </div>
              </StyledReserveWarningAlert>
            </div>
          )}
          {!isPackage && (
            <p>
              ホテルの返金：
              <span style={isRefundNg ? styles.refundNg : undefined}>{refundText}</span>
            </p>
          )}
          {displayCancelPolicy && (
            <div style={styles.cancelPolicyWrapper}>
              <span>キャンセルポリシー：</span>
              <div style={styles.cancelPolicyBox}>{displayCancelPolicy}</div>
            </div>
          )}
        </div>
        {hotel.averagePriceDetailArray.map((d, i) => (
          <div key={i} style={styles.priceDetail} data-testid={`${itemTestIdPrefix}-price-detail-${i}-row`}>
            <p data-testid={`${itemTestIdPrefix}-price-detail-${i}-title`}>{d.title}</p>
            <p data-testid={`${itemTestIdPrefix}-price-detail-${i}-value`}>{d.price}</p>
          </div>
        ))}
      </>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const getStyles = () => ({
  textDetail: {
    fontSize: '0.9em',
    paddingLeft: '0.9em',
    fontWeight: 'normal' as const
  },
  priceDetail: {
    display: 'flex' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingLeft: '100px'
  },
  refundNg: {
    fontWeight: 'bold' as const,
    color: '#959595',
    padding: '6px 10px 10px 10px'
  },
  smokeAlert: {
    fontWeight: 'bold' as const
  },
  stayPlanWarningWrapper: {
    margin: '2px 0'
  },
  cancelPolicyWrapper: {
    marginBottom: '8px'
  },
  cancelPolicyBox: {
    backgroundColor: '#eee',
    padding: '8px 12px',
    borderRadius: '6px',
    lineHeight: 1.4,
    whiteSpace: 'pre-line' as const,
    marginTop: '4px'
  }
});

export default ReserveConfirmHotelTr;
